import {forwardRef, ReactNode, useImperativeHandle, useState} from 'react';
import {useWorkerInterval} from '../../hooks/useWorkerInterval';
import {State} from './types';

export interface TimerProps {
  autoStart?: boolean;
  duration?: number; // in ms
  interval?: number; // in ms
  onEnd?: () => void;
  onTick?: (event: OnTickEvent) => void;
  render?: (options: RenderOptions) => ReactNode;
}

export const Timer = forwardRef<Timer, TimerProps>(
  ({autoStart = true, duration = 60000, interval = 1000, onEnd, onTick, render = defaultRender}, ref) => {
    const [state, setState] = useState(autoStart ? State.Running : State.Idle);
    const [passedTime, setPassedTime] = useState(0);
    const [tickCount, setTickCount] = useState(0);
    const remainingTime = duration - passedTime;

    useImperativeHandle(ref, () => ({
      pause,
      reset,
      restart,
      start,
      state,
      stop,
    }));

    useWorkerInterval(
      () => {
        const newPassedTime = passedTime + interval;
        const newRemainingTime = duration - newPassedTime;
        const newState = newRemainingTime <= 0 ? State.Stopped : state;
        const newTickCount = tickCount + 1;

        onTick?.({
          duration,
          passedTime: newPassedTime,
          remainingTime: newRemainingTime,
          state: newState,
          tickCount: newTickCount,
        });

        setPassedTime(newPassedTime);
        setTickCount(newTickCount);

        if (newRemainingTime <= 0) {
          stop();
          onEnd?.();
        }
      },
      interval,
      state === State.Running && remainingTime > 0,
    );

    if (remainingTime <= 0 && state === State.Running) {
      stop();
      onEnd?.();
      return;
    }

    const renderOptions = {duration, passedTime, remainingTime, state, tickCount};

    return render(renderOptions);

    function pause() {
      setState(State.Paused);
    }

    function reset() {
      setPassedTime(0);
      setTickCount(0);
      setState(State.Idle);
    }

    function restart() {
      setPassedTime(0);
      setTickCount(0);
      setState(State.Running);
    }

    function start() {
      setState(State.Running);
    }

    function stop() {
      setState(State.Stopped);
    }
  },
);

function defaultRender({remainingTime}: RenderOptions) {
  const remainingTimeInSeconds = Math.floor(remainingTime / 1000);
  const hours = Math.floor(remainingTimeInSeconds / 3600);
  const minutes = Math.floor(remainingTimeInSeconds / 60);
  const seconds = remainingTimeInSeconds % 60;

  return (
    <>
      {`${hours}`.padStart(2, '0')}:{`${minutes}`.padStart(2, '0')}:{`${seconds}`.padStart(2, '0')}
    </>
  );
}

export type Timer = {
  pause: () => void;
  reset: () => void;
  restart: () => void;
  start: () => void;
  state: State;
  stop: () => void;
};

export interface OnTickEvent {
  duration: number;
  passedTime: number;
  remainingTime: number;
  state: State;
  tickCount: number;
}

export interface RenderOptions {
  duration: number;
  passedTime: number;
  remainingTime: number;
  state: State;
  tickCount: number;
}
